import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { bookingsAPI, messagesAPI, noticesAPI, mediaAPI, parentAPI, accidentReportsAPI, invoicesAPI, absenceAPI, waitlistAPI, paymentsAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Toaster, toast } from 'sonner';
import { Calendar, User, Sparkles, ChevronRight, Mail, Bell, MessageSquare, Send, CheckCircle, AlertCircle, Info, X, Music, Video, FileText, Image, Download, ExternalLink, Play, Settings, UserCircle, ClipboardList, Lock, Save, Heart, PoundSterling, ShieldAlert, Phone, Users, Receipt, CalendarX, Clock, CreditCard, Star, Trash2, Ticket, MapPin } from 'lucide-react';
import Navbar from '../components/Navbar';
import AddToCalendarButton from '../components/ui/AddToCalendarButton';
import MessagesTab from '../components/parent/MessagesTab';
import NoticesTab from '../components/parent/NoticesTab';
import PaymentMethodsTab from '../components/parent/PaymentMethodsTab';
import PracticeMaterialsTab from '../components/parent/PracticeMaterialsTab';
import ProfileTab from '../components/parent/ProfileTab';
import ChildrenTab from '../components/parent/ChildrenTab';
import AccidentsTab from '../components/parent/AccidentsTab';
import AbsenceReportTab from '../components/parent/AbsenceReportTab';
import MyClassesTab from '../components/parent/MyClassesTab';
import MyCampsTab from '../components/parent/MyCampsTab';
import { formatPrice, formatDate, DAYS_OF_WEEK, formatTime } from '../lib/utils';
import api from '../lib/api';

const ParentDashboard = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [notices, setNotices] = useState([]);
  const [practiceMaterials, setPracticeMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Initialize activeTab from URL params or default to 'bookings'
  const initialTab = searchParams.get('tab') || 'bookings';
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Sync URL with tab changes
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };
  
  // Listen for URL changes (from mobile bottom nav)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      // Map URL tab values to internal tab values
      const tabMapping = {
        'bookings': 'classes',
        'payment-methods': 'profile', // Billing shows in profile section
        'profile': 'profile',
        'classes': 'classes',
        'practice': 'practice',
        'messages': 'messages',
        'notices': 'notices',
        'accidents': 'accidents',
        'absences': 'absences'
      };
      const mappedTab = tabMapping[tabFromUrl] || tabFromUrl;
      setActiveTab(mappedTab);
    }
  }, [searchParams]);
  
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  
  // New states for Phase 1 features
  const [children, setChildren] = useState([]);
  const [accidentReports, setAccidentReports] = useState([]);
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [loadingReports, setLoadingReports] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [childForm, setChildForm] = useState({});
  const [savingChild, setSavingChild] = useState(false);
  const [absences, setAbsences] = useState([]);
  const [loadingAbsences, setLoadingAbsences] = useState(false);
  const [showAbsenceForm, setShowAbsenceForm] = useState(false);
  const [absenceForm, setAbsenceForm] = useState({
    student_name: '',
    start_date: '',
    end_date: '',
    reason: '',
    notes: ''
  });
  const [submittingAbsence, setSubmittingAbsence] = useState(false);
  const [myWaitlist, setMyWaitlist] = useState([]);
  const [loadingWaitlist, setLoadingWaitlist] = useState(false);
  
  // Payment Methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [settingDefaultCard, setSettingDefaultCard] = useState(null);
  const [deletingCard, setDeletingCard] = useState(null);
  const [addingCard, setAddingCard] = useState(false);
  
  // Email verification state
  const [emailVerified, setEmailVerified] = useState(true); // Default to true to avoid flash
  const [sendingVerification, setSendingVerification] = useState(false);
  
  // Profile state
  const [profileForm, setProfileForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [attendance, setAttendance] = useState({ students: [], attendance_records: [], summary: {} });
  const [loadingAttendance, setLoadingAttendance] = useState(false);

  useEffect(() => {
    fetchData();
    if (user) {
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone: user.phone || '',
        email: user.email || '',
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    }
  }, [user]);

  useEffect(() => {
    if (activeTab === 'profile') {
      fetchAttendance();
      fetchChildren();  // Also fetch children for profile tab
    }
    if (activeTab === 'children') {
      fetchChildren();
      fetchAbsences();
    }
    if (activeTab === 'waitlist') {
      fetchMyWaitlist();
    }
    if (activeTab === 'accidents') {
      fetchAccidentReports();
    }
    if (activeTab === 'payment-methods') {
      fetchPaymentMethods();
    }
  }, [activeTab]);

  // Also load waitlist on mount for badge count
  useEffect(() => {
    fetchMyWaitlist();
    checkEmailVerification();
    
    // Check for card_added param from Stripe redirect
    const cardAdded = searchParams.get('card_added');
    const tab = searchParams.get('tab');
    
    if (tab === 'payment-methods') {
      setActiveTab('payment-methods');
      if (cardAdded === 'true') {
        toast.success('Card added successfully!');
        fetchPaymentMethods();
      } else if (cardAdded === 'false') {
        toast.info('Card addition was cancelled');
      }
      // Clean up URL params
      searchParams.delete('card_added');
      searchParams.delete('tab');
      setSearchParams(searchParams);
    }
  }, []);

  const checkEmailVerification = async () => {
    try {
      const res = await api.get('/verification/status');
      setEmailVerified(res.data.verified);
    } catch (error) {
      console.error('Failed to check email verification:', error);
      setEmailVerified(true); // Assume verified on error
    }
  };

  const handleSendVerification = async () => {
    setSendingVerification(true);
    try {
      const res = await api.post('/verification/send', { email: user.email });
      toast.success(res.data.message || 'Verification email sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send verification email');
    } finally {
      setSendingVerification(false);
    }
  };

  const fetchChildren = async () => {
    setLoadingChildren(true);
    try {
      const res = await parentAPI.getChildren();
      setChildren(res.data || []);
    } catch (error) {
      console.error('Failed to load children:', error);
    } finally {
      setLoadingChildren(false);
    }
  };

  const fetchAccidentReports = async () => {
    setLoadingReports(true);
    try {
      const res = await accidentReportsAPI.getAll();
      setAccidentReports(res.data || []);
    } catch (error) {
      console.error('Failed to load accident reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };


  const fetchAbsences = async () => {
    setLoadingAbsences(true);
    try {
      const res = await absenceAPI.getAll();
      setAbsences(res.data || []);
    } catch (error) {
      console.error('Failed to load absences:', error);
    } finally {
      setLoadingAbsences(false);
    }
  };

  const fetchMyWaitlist = async () => {
    setLoadingWaitlist(true);
    try {
      const res = await waitlistAPI.getMine();
      setMyWaitlist(res.data || []);
    } catch (error) {
      console.error('Failed to load waitlist:', error);
    } finally {
      setLoadingWaitlist(false);
    }
  };

  const handleLeaveWaitlist = async (waitlistId) => {
    try {
      await waitlistAPI.leave(waitlistId);
      toast.success('Removed from waitlist');
      fetchMyWaitlist();
    } catch (error) {
      toast.error('Failed to leave waitlist');
    }
  };

  // Payment Methods Functions
  const fetchPaymentMethods = async () => {
    setLoadingPaymentMethods(true);
    try {
      const res = await paymentsAPI.getPaymentMethods();
      setPaymentMethods(res.data || []);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
      setPaymentMethods([]);
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  const handleSetDefaultCard = async (paymentMethodId) => {
    setSettingDefaultCard(paymentMethodId);
    try {
      await paymentsAPI.setDefaultPaymentMethod(paymentMethodId);
      toast.success('Default payment method updated');
      fetchPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update default');
    } finally {
      setSettingDefaultCard(null);
    }
  };

  const handleDeleteCard = async (paymentMethodId) => {
    if (!window.confirm('Are you sure you want to remove this card?')) return;
    
    setDeletingCard(paymentMethodId);
    try {
      await paymentsAPI.deletePaymentMethod(paymentMethodId);
      toast.success('Payment method removed');
      fetchPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove card');
    } finally {
      setDeletingCard(null);
    }
  };

  const handleAddCard = async () => {
    setAddingCard(true);
    try {
      const res = await paymentsAPI.addPaymentMethodSession();
      // Redirect to Stripe Checkout for adding a card
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate card addition');
      setAddingCard(false);
    }
  };

  const handleReportAbsence = async () => {
    if (!absenceForm.student_name || !absenceForm.start_date || !absenceForm.end_date || !absenceForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmittingAbsence(true);
    try {
      await absenceAPI.report(absenceForm);
      toast.success('Absence reported successfully');
      setShowAbsenceForm(false);
      setAbsenceForm({ student_name: '', start_date: '', end_date: '', reason: '', notes: '' });
      fetchAbsences();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report absence');
    } finally {
      setSubmittingAbsence(false);
    }
  };

  const handleCancelAbsence = async (absenceId) => {
    try {
      await absenceAPI.cancel(absenceId);
      toast.success('Absence report cancelled');
      fetchAbsences();
    } catch (error) {
      toast.error('Failed to cancel absence');
    }
  };

  const handleSaveChild = async () => {
    if (!editingChild) return;
    setSavingChild(true);
    try {
      await parentAPI.updateChild(editingChild.name, childForm);
      toast.success('Information updated!');
      setEditingChild(null);
      fetchChildren();
    } catch (error) {
      toast.error('Failed to update information');
    } finally {
      setSavingChild(false);
    }
  };

  const handleAcknowledgeReport = async (reportId) => {
    try {
      await accidentReportsAPI.acknowledge(reportId);
      toast.success('Report acknowledged');
      fetchAccidentReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to acknowledge report');
    }
  };

  const fetchData = async () => {
    try {
      const [bookingsRes, messagesRes, noticesRes, materialsRes] = await Promise.all([
        bookingsAPI.getAll(),
        messagesAPI.getAll(),
        noticesAPI.getAll(),
        mediaAPI.getPracticeMaterials()
      ]);
      setBookings(bookingsRes.data);
      setMessages(messagesRes.data);
      setNotices(noticesRes.data);
      setPracticeMaterials(materialsRes.data?.materials || []);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendance = async () => {
    setLoadingAttendance(true);
    try {
      const res = await api.get('/auth/my-attendance');
      setAttendance(res.data || { students: [], attendance_records: [], summary: {} });
    } catch (error) {
      console.error('Failed to load attendance:', error);
    } finally {
      setLoadingAttendance(false);
    }
  };

  const handleSaveProfile = async () => {
    if (profileForm.new_password && profileForm.new_password !== profileForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    setSavingProfile(true);
    try {
      const updateData = {
        first_name: profileForm.first_name,
        last_name: profileForm.last_name,
        phone: profileForm.phone,
        email: profileForm.email
      };
      
      if (profileForm.new_password) {
        updateData.current_password = profileForm.current_password;
        updateData.new_password = profileForm.new_password;
      }
      
      const res = await api.put('/auth/profile', updateData);
      toast.success(res.data.message || 'Profile updated successfully');
      
      // Clear password fields
      setProfileForm(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: ''
      }));
      
      // Update auth context if updateUser is available
      if (updateUser && res.data.user) {
        updateUser(res.data.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await bookingsAPI.cancel(bookingId);
      toast.success('Booking cancelled');
      fetchData();
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const handleMarkMessageRead = async (messageId) => {
    try {
      await messagesAPI.markRead(messageId);
      setMessages(messages.map(m => 
        m.id === messageId ? { ...m, read_by: [...(m.read_by || []), user.id] } : m
      ));
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const handleMarkNoticeRead = async (noticeId) => {
    try {
      await noticesAPI.markRead(noticeId);
      setNotices(notices.map(n => 
        n.id === noticeId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark notice as read');
    }
  };

  const handleSendReply = async () => {
    if (!replyContent.trim() || !selectedMessage) return;
    
    setSendingReply(true);
    try {
      await messagesAPI.reply(selectedMessage.id, replyContent);
      toast.success('Reply sent to UPA team');
      setReplyContent('');
      setSelectedMessage(null);
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const upcomingBookings = bookings.filter(b => b.status === 'confirmed' || b.status === 'pending');
  const unreadMessages = messages.filter(m => !m.read_by?.includes(user?.id));
  const unreadNotices = notices.filter(n => !n.is_read);


  return (
    <div className="min-h-screen bg-[#050505] text-white pb-20 md:pb-8">
      <Navbar />
      <Toaster position="top-center" richColors />
      
      <div className="max-w-6xl mx-auto px-4 py-8 pb-24 md:pb-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-[#7000FF] to-[#FF007F] rounded-full flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-bold text-white">
              Welcome back, {user?.first_name}!
            </h1>
            <p className="text-white/60">Manage your class bookings and stay updated</p>
          </div>
        </div>

        {/* Email Verification Banner */}
        {!emailVerified && (
          <div className="mb-6 p-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Mail className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="font-medium text-amber-200">Verify your email address</p>
                  <p className="text-sm text-amber-200/70">
                    Verify your email to receive important updates about your classes
                  </p>
                </div>
              </div>
              <Button
                onClick={handleSendVerification}
                disabled={sendingVerification}
                className="bg-amber-500 hover:bg-amber-600 text-black font-medium"
              >
                {sendingVerification ? (
                  <>
                    <span className="mr-2 animate-spin">⏳</span>
                    Sending...
                  </>
                ) : (
                  'Send Verification Email'
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-8">
          <Card className="bg-[#111] border-[#333] hover:border-[#FF007F]/50 transition-colors">
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
                <div>
                  <p className="text-xs md:text-sm text-white/50">Active Bookings</p>
                  <p className="text-2xl md:text-3xl font-bold text-[#FF007F]">{upcomingBookings.length}</p>
                </div>
                <Calendar className="w-8 h-8 md:w-10 md:h-10 text-[#FF007F]/30 hidden md:block" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111] border-[#333] cursor-pointer hover:border-[#7000FF]/50 transition-colors"
                onClick={() => handleTabChange('messages')}>
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
                <div>
                  <p className="text-xs md:text-sm text-white/50">Messages</p>
                  <p className="text-2xl md:text-3xl font-bold text-[#7000FF]">{unreadMessages.length} new</p>
                </div>
                <Mail className="w-8 h-8 md:w-10 md:h-10 text-[#7000FF]/30 hidden md:block" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111] border-[#333] cursor-pointer hover:border-[#FFD700]/50 transition-colors"
                onClick={() => handleTabChange('notices')}>
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
                <div>
                  <p className="text-xs md:text-sm text-white/50">Notices</p>
                  <p className="text-2xl md:text-3xl font-bold text-[#FFD700]">{unreadNotices.length} unread</p>
                </div>
                <Bell className="w-8 h-8 md:w-10 md:h-10 text-[#FFD700]/30 hidden md:block" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#111] border-[#333] cursor-pointer hover:border-green-500/50 transition-colors"
                onClick={() => handleTabChange('practice')}>
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
                <div>
                  <p className="text-xs md:text-sm text-white/50">Practice</p>
                  <p className="text-2xl md:text-3xl font-bold text-green-400">{practiceMaterials.length}</p>
                </div>
                <Music className="w-8 h-8 md:w-10 md:h-10 text-green-500/30 hidden md:block" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs - Simplified Navigation */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="mb-6 bg-[#111] border border-[#333] flex-wrap gap-1 h-auto p-2">
            <TabsTrigger value="profile" className="data-[state=active]:bg-[#FF007F] data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-profile">
              <UserCircle className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Profile</span>
              <span className="xs:hidden">Prof</span>
            </TabsTrigger>
            <TabsTrigger value="classes" className="data-[state=active]:bg-[#7000FF] data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-classes">
              <Calendar className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">My Classes</span>
              <span className="xs:hidden">Classes</span>
            </TabsTrigger>
            <TabsTrigger value="camps" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-camps">
              <Star className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">My Camps</span>
              <span className="xs:hidden">Camps</span>
            </TabsTrigger>
            <TabsTrigger value="practice" className="data-[state=active]:bg-green-600 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-practice">
              <Music className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Practice Materials</span>
              <span className="sm:hidden">Practice</span>
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-messages">
              <Mail className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Messages</span>
              <span className="xs:hidden">Msgs</span>
              {unreadMessages.length > 0 && <Badge className="ml-1 bg-red-500 text-xs">{unreadMessages.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="notices" className="data-[state=active]:bg-[#FFD700] data-[state=active]:text-black text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-notices">
              <Bell className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Noticeboard</span>
              <span className="sm:hidden">Notices</span>
              {unreadNotices.length > 0 && <Badge className="ml-1 bg-red-500 text-xs">{unreadNotices.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="accidents" className="data-[state=active]:bg-red-600 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-accidents">
              <ShieldAlert className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Accident Reports</span>
              <span className="sm:hidden">Accidents</span>
            </TabsTrigger>
            <TabsTrigger value="absences" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-3" data-testid="tab-absences">
              <CalendarX className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Report Absence</span>
              <span className="sm:hidden">Absence</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab - Includes Student Info and Payment Methods */}
          <TabsContent value="profile">
            {!user ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-4 border-[#FF007F] border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Student Information */}
                <Card className="bg-[#111] border-[#333]">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <User className="w-5 h-5 text-[#FF007F]" />
                      Student Information
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingChildren ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin w-8 h-8 border-4 border-[#FF007F] border-t-transparent rounded-full" />
                      </div>
                    ) : (
                      <ChildrenTab 
                        children={children || []}
                        loading={loading}
                        onUpdate={() => fetchChildren()}
                      />
                    )}
                  </CardContent>
                </Card>
                
                {/* Payment Methods */}
                <Card className="bg-[#111] border-[#333]">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <CreditCard className="w-5 h-5 text-emerald-400" />
                      Payment Methods
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PaymentMethodsTab 
                      paymentMethods={paymentMethods}
                      loading={loadingPaymentMethods}
                      onRefresh={fetchPaymentMethods}
                      settingDefaultCard={settingDefaultCard}
                      setSettingDefaultCard={setSettingDefaultCard}
                      deletingCard={deletingCard}
                      setDeletingCard={setDeletingCard}
                      addingCard={false}
                      onAddCard={() => {}}
                    />
                  </CardContent>
                </Card>
                
                {/* Account Settings */}
                <ProfileTab 
                  profileForm={profileForm || {}}
                  setProfileForm={setProfileForm}
                  savingProfile={savingProfile}
                  handleSaveProfile={handleSaveProfile}
                  attendance={attendance || { students: [], attendance_records: [], summary: {} }}
                  loadingAttendance={loadingAttendance}
                />
              </div>
            )}
          </TabsContent>

          {/* My Classes Tab */}
          <TabsContent value="classes">
            <MyClassesTab children={children} />
          </TabsContent>

          {/* My Camps Tab */}
          <TabsContent value="camps">
            <MyCampsTab />
          </TabsContent>

          {/* Practice Materials Tab */}
          <TabsContent value="practice">
            <PracticeMaterialsTab practiceMaterials={practiceMaterials} />
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <MessagesTab 
              messages={messages} 
              user={user}
              onMarkRead={(messageId) => {
                setMessages(messages.map(m => 
                  m.id === messageId ? { ...m, read_by: [...(m.read_by || []), user.id] } : m
                ));
              }}
              onRefresh={fetchData}
            />
          </TabsContent>

          {/* Noticeboard Tab */}
          <TabsContent value="notices">
            <NoticesTab 
              notices={notices}
              onMarkRead={(noticeId) => {
                setNotices(notices.map(n => 
                  n.id === noticeId ? { ...n, is_read: true } : n
                ));
              }}
            />
          </TabsContent>

          {/* Accident Reports Tab */}
          <TabsContent value="accidents">
            <AccidentsTab
              accidentReports={accidentReports}
              loadingReports={loadingReports}
              handleAcknowledgeReport={handleAcknowledgeReport}
            />
          </TabsContent>

          {/* Absence Reporting Tab */}
          <TabsContent value="absences">
            <AbsenceReportTab user={user} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ParentDashboard;
